import React, { ChangeEvent, FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import dayjs, { ManipulateType } from 'dayjs';
import styled from 'styled-components';
import { flatten, filter } from 'lodash-es';
import { parse } from 'query-string';
import { useLocation } from 'react-router-dom';

import { DateDropdown, FilterIcon, FiltersPopover, palette, SearchInput } from '@dev/ee-components';

import { LONG_DATE_FORMAT } from '@/services/constants';
import { debounce } from '@/services/tools/debounce';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { GetCampaigns, UnpickAllCampaigns } from '@/store/campaigns/actions';
import useMobile from '@/services/hooks/useMobile';
import { StyledProps } from '@/components/Campaigns/CampaignsInterface';
import { useDateDropdownValue } from '@/services/hooks/useDateDropdownValue';
import { useRequestParams } from '../../hooks/useRequestParams';
import { useDateDropdownOptions } from '../../../hooks/useDateDropdownOptions';
import { useCampaignStatuses } from '../../hooks/useCampaignStatuses';
import { MobileFilterTabsProps } from '../../../CampaignsInterface';

const SearchInputWrapper = styled.div<StyledProps>`
    width: ${({ isMobile }): string => (isMobile ? '100%' : '280px')};
    ${({ isMobile }): string =>
        isMobile &&
        `
    & > div {
        border: 1px solid ${palette.gray[3]};
    }
    `}
`;

const TableFiltersWrapper = styled.div<StyledProps>`
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    padding: ${({ isMobile }): string => !isMobile && '25px 48px 34px'};
    margin: ${({ isMobile }): string => isMobile && '32px 0 20px 0'};
`;

const FiltersContainer = styled.div`
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
    margin-left: 8px;
    padding: 11px 11px;
`;

const SearchComponent: FC = (): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const requestParams = useRequestParams();
    const isMobile = useMobile();

    const changeSearchInputVal = (event?: ChangeEvent<HTMLInputElement>): void => {
        const search = event ? event.currentTarget.value : '';

        debounce((): void => {
            dispatch(UnpickAllCampaigns());
            dispatch(GetCampaigns({ ...requestParams, search }));
        }, 500);
    };

    return (
        <SearchInputWrapper isMobile={isMobile}>
            <SearchInput
                id="searchCampaigns"
                name="search"
                placeholder={t('searchforcampaigns')}
                type="text"
                onChange={changeSearchInputVal}
                onDelete={changeSearchInputVal}
                defaultValue={requestParams.search || ''}
            />
        </SearchInputWrapper>
    );
};

const TableFilters: FC = (): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMobile = useMobile();
    const requestParams = useRequestParams();
    const dateFilterData = useDateDropdownOptions();
    const statusOptions = useCampaignStatuses();
    const location = useLocation();
    const query = parse(location.search);
    const startDate = query?.from?.toString();
    const endDate = query?.to?.toString();
    const dateValue = useDateDropdownValue(false, DateDropdownOptions.All);

    const calendarChange = (from: string, to: string): void => {
        const fromFormated = dayjs(from).format(LONG_DATE_FORMAT);
        const toFormated = dayjs(to).format(LONG_DATE_FORMAT);

        dispatch(UnpickAllCampaigns());
        dispatch(GetCampaigns({ ...requestParams, from: fromFormated, to: toFormated }));
    };

    const dateFilterClick = (
        event: MouseEvent<HTMLInputElement>,
        date: { label: string; value: number | string }
    ): void => {
        const to = date.value === DateDropdownOptions.All ? null : dayjs().format(LONG_DATE_FORMAT);
        const from =
            date.value === DateDropdownOptions.All
                ? null
                : dayjs()
                      .subtract(date.value as number, 'days' as ManipulateType)
                      .format(LONG_DATE_FORMAT);

        dispatch(UnpickAllCampaigns());
        dispatch(GetCampaigns({ ...requestParams, from, to }));
    };

    const getMobileFilterTabs = (): MobileFilterTabsProps[] => {
        const defaultValue = requestParams?.status ? flatten([requestParams?.status]) : [];

        return [
            {
                label: t('daterange'),
                defaultValue: dateValue.toString(),
                options: dateFilterData,
                type: 'date',
                startDate,
                endDate,
            },
            {
                defaultCheckboxValue: defaultValue,
                label: t('status'),
                options: statusOptions,
                type: 'checkbox',
            },
        ];
    };

    const mobileFilterClick = (filtersObj, momentFrom, momentTo): void => {
        const dateValue = filtersObj[0];
        const statusValue = filtersObj[1];
        let from;
        let to;
        if (dateValue === DateDropdownOptions.Calendar) {
            from = dayjs(momentFrom).format(LONG_DATE_FORMAT);
            to = dayjs(momentTo).format(LONG_DATE_FORMAT);
        } else {
            to = dateValue === DateDropdownOptions.All ? null : dayjs().format(LONG_DATE_FORMAT);
            from =
                dateValue === DateDropdownOptions.All
                    ? null
                    : dayjs()
                          .subtract(dateValue as number, 'days' as ManipulateType)
                          .format(LONG_DATE_FORMAT);
        }

        const filteredStatusValue = filter(statusValue, (el): boolean => el !== '');
        dispatch(UnpickAllCampaigns());
        dispatch(GetCampaigns({ ...requestParams, from, to, status: filteredStatusValue || requestParams.status }));
    };

    if (isMobile) {
        return (
            <TableFiltersWrapper isMobile>
                <SearchComponent />
                <FiltersContainer>
                    <FiltersPopover
                        onApply={mobileFilterClick}
                        tabs={getMobileFilterTabs()}
                        title={t('filters')}
                        translations={{
                            apply: t('apply'),
                            close: t('close'),
                        }}
                    >
                        <FilterIcon color={palette.gray[6]} width={20} height={20} />
                    </FiltersPopover>
                </FiltersContainer>
            </TableFiltersWrapper>
        );
    }

    return (
        <TableFiltersWrapper>
            <DateDropdown
                options={dateFilterData}
                defaultPlaceholder={t('all')}
                value={dateValue}
                type="input"
                returnRawData
                calendarOnChange={calendarChange}
                onItemClick={dateFilterClick}
                buttonVariant="tertiary"
                startDate={startDate}
                endDate={endDate}
            />
            <SearchComponent />
        </TableFiltersWrapper>
    );
};

export default TableFilters;
